import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import {
  ArchiveBoxIcon,
  ArchiveBoxXMarkIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { TanTable } from '../../../components/TanTable';
import Notification, { NotificationContainer } from '../../../components/Notification';
import contractsService from '../../../services/compliance/contracts.service';
import thirdPartiesService from '../../../services/compliance/thirdParties.service';
import permissionsService, { AzureADUser } from '../../../services/permissions.service';
import { useComplianceAccess } from '../../../hooks/useComplianceAccess';
import { Contract, ContractStatus, ThirdParty } from '../../../types/compliance';
import ContractDrawer from './ContractDrawer';
import ContractImportDrawer from './ContractImportDrawer';
import { effectiveStatus, formatTermLength, isMonthly } from './contractTerm';
import { formatDateOnly, rowHighlight } from './contractAttention';

const STATUS_BADGE: Record<ContractStatus, string> = {
  draft: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  active: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  expired: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
  terminated: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
  pending_renewal: 'bg-yellow-200 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
};

const STATUS_LABEL: Record<ContractStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  expired: 'Expired',
  terminated: 'Terminated',
  pending_renewal: 'Pending Renewal',
};

// URL filters (set by dashboard / audit-log links) and how each reads as a chip.
const FILTER_CHIPS: { key: string; label: (v: string) => string }[] = [
  { key: 'status', label: (v) => `Status: ${STATUS_LABEL[v as ContractStatus] ?? v}` },
  { key: 'category', label: (v) => `Category: ${v}` },
  { key: 'ending_after', label: (v) => `Ending from: ${formatDateOnly(v)}` },
  { key: 'ending_before', label: (v) => `Ending by: ${formatDateOnly(v)}` },
  { key: 'value_min', label: (v) => `Value ≥ $${Number(v).toLocaleString('en-US')}` },
  { key: 'value_max', label: (v) => `Value ≤ $${Number(v).toLocaleString('en-US')}` },
];

// Opaque colors: the pinned Actions cell is painted with the row class too.
// Gray = 1-month contract (always ends within a month); amber = ends within
// 30 days or past.
const ROW_HIGHLIGHT_CLASS: Record<'monthly' | 'expiring' | 'none', string> = {
  monthly: 'bg-gray-100 dark:bg-gray-800',
  expiring: 'bg-amber-100 dark:bg-amber-950',
  none: '',
};

const DEFAULT_SORTING = [{ id: 'end_date', desc: false }];

// Only contracts that have ended can be archived (matches the backend).
const ARCHIVABLE_STATUSES: ContractStatus[] = ['expired', 'terminated'];

// Archived contracts are hidden unless the URL says `archived=show`. The hidden
// state reads as a chip, so removing it (or Clear filters) shows them.
const ARCHIVED_PARAM = 'archived';
const ARCHIVED_CHIP = { key: ARCHIVED_PARAM, text: 'Archived hidden' };

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

interface Props {
  /** When rendered inside ContractsPage's tab strip, hide the page title
   *  and cross-nav buttons (they're rendered by the tab header). */
  embedded?: boolean;
}


export default function ContractsList({ embedded = false }: Props = {}) {
  const { isManager, hasAny, userOid, loading: accessLoading } = useComplianceAccess();
  const canTerminate = isManager;
  // Managers can edit any contract; other compliance users only theirs.
  // Matches the backend owner-scoping check.
  const canEditContract = (c: Contract): boolean =>
    isManager || (hasAny && c.owner_oid === userOid);
  const canArchive = (c: Contract): boolean =>
    canEditContract(c) && !c.is_archived && ARCHIVABLE_STATUSES.includes(c.status);
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<Contract[]>([]);
  const [thirdPartyMap, setThirdPartyMap] = useState<Record<string, string>>({});
  const [adUsers, setAdUsers] = useState<AzureADUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorNotification, setErrorNotification] =
    useState<{ title: string; message: string } | null>(null);
  const [successNotification, setSuccessNotification] =
    useState<{ title: string; message: string } | null>(null);
  const [drawer, setDrawer] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    id: string | null;
  }>({ open: false, mode: 'create', id: null });
  const [importOpen, setImportOpen] = useState(false);
  const [selected, setSelected] = useState<Contract[]>([]);
  // Bumped after a bulk archive so the table clears its checkboxes too.
  const [selectionResetKey, setSelectionResetKey] = useState(0);

  // Filter state — URL-bound so the selection is bookmarkable / shareable.
  // Mirrors the audit page's useSearchParams pattern.
  type Filters = {
    status?: ContractStatus;
    category?: string;
    value_min?: string;
    value_max?: string;
    ending_before?: string;
    ending_after?: string;
  };
  // Keyed on the filter values themselves, so unrelated URL changes (the
  // My/All switch, ?id=) don't trigger a reload.
  const statusParam = searchParams.get('status');
  const categoryParam = searchParams.get('category');
  const valueMinParam = searchParams.get('value_min');
  const valueMaxParam = searchParams.get('value_max');
  const endingBeforeParam = searchParams.get('ending_before');
  const endingAfterParam = searchParams.get('ending_after');
  const filters: Filters = useMemo(() => ({
    status: (statusParam as ContractStatus) || undefined,
    category: categoryParam || undefined,
    value_min: valueMinParam || undefined,
    value_max: valueMaxParam || undefined,
    ending_before: endingBeforeParam || undefined,
    ending_after: endingAfterParam || undefined,
  }), [statusParam, categoryParam, valueMinParam, valueMaxParam, endingBeforeParam, endingAfterParam]);

  // "My contracts" (owner or renewal owner) is the everyday default; managers
  // start on everything. An explicit ?scope= in the URL always wins.
  const scopeParam = searchParams.get('scope');
  const scope: 'mine' | 'all' =
    scopeParam === 'mine' || scopeParam === 'all' ? scopeParam : isManager ? 'all' : 'mine';
  const setScope = (next: 'mine' | 'all') => {
    const params = new URLSearchParams(searchParams);
    params.set('scope', next);
    setSearchParams(params, { replace: true });
  };

  const showArchived = searchParams.get(ARCHIVED_PARAM) === 'show';
  const filterChips = FILTER_CHIPS.flatMap(({ key, label }) => {
    const value = searchParams.get(key);
    return value ? [{ key, text: label(value) }] : [];
  });
  const activeChips = showArchived ? filterChips : [...filterChips, ARCHIVED_CHIP];
  const removeFilters = (keys: string[]) => {
    const params = new URLSearchParams(searchParams);
    keys.forEach((k) => (k === ARCHIVED_PARAM ? params.set(k, 'show') : params.delete(k)));
    setSearchParams(params, { replace: true });
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await contractsService.list({
        status: filters.status,
        category: filters.category || undefined,
        value_min: filters.value_min || undefined,
        value_max: filters.value_max || undefined,
        ending_before: filters.ending_before || undefined,
        ending_after: filters.ending_after || undefined,
        include_archived: showArchived,
      });
      setData(rows);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to load contracts', message: msg });
    } finally {
      setLoading(false);
    }
  }, [filters, showArchived]);

  // Cross-link restoration: if the URL carries ?id=<contract-id> (audit log,
  // dashboard "expiring soon" table, etc.), open the drawer in edit mode on
  // mount. We strip the query param afterwards so a refresh-to-reopen isn't
  // sticky forever.
  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      setDrawer({ open: true, mode: 'edit', id });
      searchParams.delete('id');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Reload whenever the URL filters change (e.g. a chip is removed).
  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    thirdPartiesService
      .list()
      .then((tps: ThirdParty[]) => {
        const map: Record<string, string> = {};
        tps.forEach((tp) => {
          map[tp.id] = tp.name;
        });
        setThirdPartyMap(map);
      })
      .catch(() => {
        // Third party resolution is best-effort; don't block the list.
      });
    permissionsService
      .getEnabledUsers()
      .then((users) => {
        setAdUsers(users);
      })
      .catch((error: any) => {
        const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
        setErrorNotification({ title: 'Failed to load users', message: msg });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userNameByOid = useMemo(() => {
    const map = new Map<string, string>();
    adUsers.forEach((u) => {
      map.set(u.user_id, u.display_name);
    });
    return map;
  }, [adUsers]);

  // Always a fresh array, and rebuilt when the name lookups land: TanStack
  // caches each row's cell values until `data` changes, so without this the
  // Third Party / Owner cells keep the ids they showed before the names loaded.
  const visibleData = useMemo(
    () =>
      scope === 'mine'
        ? data.filter((c) => c.owner_oid === userOid || c.renewal_owner_oid === userOid)
        : [...data],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, scope, userOid, thirdPartyMap, userNameByOid],
  );

  const notifyError = (title: string, error: any) => {
    const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
    setErrorNotification({ title, message: msg });
  };

  const handleArchive = async (row: Contract, archive: boolean) => {
    try {
      if (archive) await contractsService.archive(row.id);
      else await contractsService.unarchive(row.id);
      setSuccessNotification({
        title: archive ? 'Archived' : 'Unarchived',
        message: `"${row.title}" has been ${archive ? 'archived' : 'unarchived'}.`,
      });
      loadData();
    } catch (error: any) {
      notifyError(archive ? 'Failed to Archive' : 'Failed to Unarchive', error);
    }
  };

  const handleBulkArchive = async () => {
    const ids = selected.map((c) => c.id);
    try {
      const { archived, skipped } = await contractsService.bulkArchive(ids);
      const n = archived.length;
      const reasons = Array.from(new Set(skipped.map((s) => s.reason))).join('; ');
      setSuccessNotification({
        title: 'Archive complete',
        message:
          `Archived ${n} contract${n === 1 ? '' : 's'}` +
          (skipped.length ? `, skipped ${skipped.length} (${reasons})` : '') + '.',
      });
      setSelected([]);
      setSelectionResetKey((k) => k + 1);
      loadData();
    } catch (error: any) {
      notifyError('Failed to Archive', error);
    }
  };

  const handleDelete = useCallback(
    async (row: Contract) => {
      if (!window.confirm(`Are you sure you want to delete "${row.title}"?`)) return;
      try {
        await contractsService.remove(row.id);
        setSuccessNotification({
          title: 'Deleted Successfully',
          message: `Contract "${row.title}" has been deleted.`,
        });
        loadData();
      } catch (error: any) {
        const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
        setErrorNotification({ title: 'Failed to Delete', message: msg });
      }
    },
    [loadData],
  );

  const columns = useMemo<ColumnDef<Contract>[]>(
    () => [
      {
        accessorKey: 'title',
        header: () => <span className="pl-3">Title</span>,
        enableSorting: true,
        size: 240,
        cell: ({ row }) => (
          <span className="pl-3 inline-flex items-center gap-2">
            <span className="font-semibold text-blue-600 dark:text-blue-400">
              {row.original.title}
            </span>
            {isMonthly(row.original) && (
              <span className="shrink-0 rounded px-1.5 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                Monthly
              </span>
            )}
          </span>
        ),
      },
      {
        // Value is the display name so the column filter, sorting, search
        // and export all show names. The id stays 'third_party_id' so saved
        // table layouts keep working.
        id: 'third_party_id',
        accessorFn: (c) => thirdPartyMap[c.third_party_id] || c.third_party_id,
        header: 'Third Party',
        enableSorting: true,
        size: 200,
        cell: ({ getValue }) => <span>{getValue<string>()}</span>,
      },
      {
        // Shown status, so filter/sort agree with the badge (a month-to-month
        // Pending Renewal reads as Active).
        id: 'status',
        accessorFn: (c) => effectiveStatus(c),
        header: 'Status',
        enableSorting: true,
        size: 140,
        cell: ({ row }) => {
          const s = effectiveStatus(row.original);
          return (
            <span className="inline-flex items-center gap-1">
              <span className={`px-2 py-0.5 text-xs font-medium rounded ${STATUS_BADGE[s] || ''}`}>
                {s.replace('_', ' ')}
              </span>
              {row.original.is_archived && (
                <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  Archived
                </span>
              )}
            </span>
          );
        },
      },
      {
        accessorKey: 'start_date',
        header: 'Start Date',
        enableSorting: true,
        size: 120,
        cell: ({ row }) => <span>{formatDate(row.original.start_date)}</span>,
      },
      {
        accessorKey: 'end_date',
        header: 'End Date',
        enableSorting: true,
        size: 120,
        cell: ({ row }) => {
          const ed = row.original.end_date;
          if (!ed) return <span>-</span>;
          const cutoff = new Date();
          cutoff.setDate(cutoff.getDate() + 30);
          const isExpiring = new Date(ed) <= cutoff;
          return (
            <span className={isExpiring ? 'font-bold text-red-600 dark:text-red-400' : ''}>
              {formatDate(ed)}
            </span>
          );
        },
      },
      {
        id: 'term_length',
        header: 'Length',
        enableSorting: false,
        size: 90,
        cell: ({ row }) => (
          <span>
            {formatTermLength(row.original.term_length, row.original.term_unit) || '—'}
          </span>
        ),
      },
      {
        accessorKey: 'contract_value',
        header: 'Total Value',
        enableSorting: true,
        size: 100,
        cell: ({ row }) => {
          const v = row.original.contract_value;
          if (v === null || v === undefined || v === '') return <span>—</span>;
          const n = Number(v);
          if (Number.isNaN(n)) return <span>—</span>;
          const display =
            n >= 1_000_000
              ? `$${(n / 1_000_000).toFixed(1)}M`
              : n >= 1_000
              ? `$${(n / 1_000).toFixed(1)}K`
              : `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
          return <span className="tabular-nums">{display}</span>;
        },
      },
      {
        accessorKey: 'monthly_cost',
        header: 'Monthly Cost',
        enableSorting: true,
        size: 100,
        sortingFn: (a, b) =>
          Number(a.original.monthly_cost ?? -1) - Number(b.original.monthly_cost ?? -1),
        cell: ({ row }) => {
          const v = row.original.monthly_cost;
          if (v === null || v === undefined || v === '') return <span>—</span>;
          const n = Number(v);
          if (Number.isNaN(n)) return <span>—</span>;
          return (
            <span className="tabular-nums">
              {n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
            </span>
          );
        },
      },
      {
        accessorKey: 'department',
        header: 'Department',
        enableSorting: true,
        size: 120,
        cell: ({ row }) => <span>{row.original.department || '—'}</span>,
      },
      {
        // Display name as the value, same reasoning as Third Party above.
        id: 'owner_oid',
        accessorFn: (c) => (c.owner_oid ? userNameByOid.get(c.owner_oid) || c.owner_oid : ''),
        header: 'Owner',
        enableSorting: true,
        size: 100,
        cell: ({ getValue }) => <span>{getValue<string>() || '—'}</span>,
      },
      {
        accessorKey: 'auto_renewal',
        header: 'Auto',
        enableSorting: true,
        size: 60,
        cell: ({ row }) =>
          row.original.auto_renewal ? (
            <span
              title="Auto-renewal"
              className="px-1.5 py-0.5 text-[0.625rem] font-medium rounded bg-info text-info-fg"
            >
              Auto
            </span>
          ) : (
            <span className="text-gray-400">—</span>
          ),
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div className="flex gap-2 justify-end">
            {canEditContract(row.original) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDrawer({ open: true, mode: 'edit', id: row.original.id });
                }}
                className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                title="Edit"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
            )}
            {canArchive(row.original) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleArchive(row.original, true);
                }}
                className="p-1 text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white transition-colors"
                title="Archive"
              >
                <ArchiveBoxIcon className="w-4 h-4" />
              </button>
            )}
            {row.original.is_archived && canEditContract(row.original) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleArchive(row.original, false);
                }}
                className="p-1 text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white transition-colors"
                title="Unarchive"
              >
                <ArchiveBoxXMarkIcon className="w-4 h-4" />
              </button>
            )}
            {canTerminate && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(row.original);
                }}
                className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                title="Delete"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleDelete, thirdPartyMap, userNameByOid, isManager, hasAny, userOid, canTerminate, loadData],
  );

  return (
    <div className="h-full w-full p-6 pb-24">
      {!embedded && (
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Contracts</h1>
            <p className="text-sm text-muted">
              Track contracts, obligations, and documents across third parties.
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to="/compliance/contracts?tab=dashboard"
              className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700"
            >
              View Dashboard
            </Link>
          </div>
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden text-sm">
          {(['mine', 'all'] as const).map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={scope === s}
              onClick={() => setScope(s)}
              className={`px-3 py-1.5 ${
                scope === s
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {s === 'mine' ? 'My contracts' : 'All contracts'}
            </button>
          ))}
        </div>
        {activeChips.map((chip) => (
          <span
            key={chip.key}
            className="inline-flex items-center gap-1 rounded-full bg-blue-50 dark:bg-blue-900/40 px-3 py-1 text-sm text-blue-800 dark:text-blue-200"
          >
            {chip.text}
            <button
              type="button"
              aria-label={`Remove filter ${chip.text}`}
              onClick={() => removeFilters([chip.key])}
              className="rounded-full p-0.5 hover:bg-blue-100 dark:hover:bg-blue-800"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
        {activeChips.length > 0 && (
          <button
            type="button"
            onClick={() => removeFilters(activeChips.map((c) => c.key))}
            className="text-sm text-gray-600 dark:text-gray-300 hover:underline"
          >
            Clear filters
          </button>
        )}
        {selected.length > 0 && (
          <button
            type="button"
            onClick={handleBulkArchive}
            className="ml-auto inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded bg-gray-700 text-white hover:bg-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500"
          >
            <ArchiveBoxIcon className="w-4 h-4" />
            Archive selected ({selected.length})
          </button>
        )}
      </div>

      {scope === 'mine' && !loading && !accessLoading && visibleData.length === 0 && (
        <p className="mb-3 text-sm text-muted">
          You don&apos;t have any contracts yet{filterChips.length > 0 ? ' matching these filters' : ''}.{' '}
          <button
            type="button"
            onClick={() => setScope('all')}
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            Show all contracts
          </button>
        </p>
      )}

      {/* TanTable's column filters + global search cover filtering. URL
          params (status, category, value_min, ending_before, etc.) still
          flow into `filters` and pre-filter the initial backend load, so
          dashboard and audit-log cross-links keep working. */}

      <TanTable
        data={visibleData}
        columns={columns}
        loading={loading || accessLoading}
        defaultSorting={DEFAULT_SORTING}
        settingsKey="compliance-contracts-table"
        fieldName="compliance-contracts-table"
        searchPlaceholder="Search contracts..."
        onRowClick={(row) => setDrawer({ open: true, mode: 'edit', id: row.id })}
        // Ending within 30 days, past due, or a 1-month contract. Opaque
        // colors: the pinned Actions cell is painted with this class too.
        getRowClassName={(row) => ROW_HIGHLIGHT_CLASS[rowHighlight(row) ?? 'none']}
        getRowTitle={(row) => (isMonthly(row) ? 'Monthly contract (1-month length)' : undefined)}
        getRowId={(row) => row.id}
        enableRowSelection={true}
        canSelectRow={canArchive}
        onSelectionChange={setSelected}
        selectionResetKey={selectionResetKey}
        enableGrouping={false}
        enableColumnFilters={true}
        cardTitle="Contracts"
        lockedColumns={[{ id: 'actions', side: 'right' }]}
        showEditFiltersButton={true}
        editFiltersButtonIcon="plus"
        editFiltersButtonTooltip="New Contract"
        onEditFilters={() => setDrawer({ open: true, mode: 'create', id: null })}
      />

      <ContractDrawer
        mode={drawer.mode}
        contractId={drawer.id}
        open={drawer.open}
        adUsers={adUsers}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        onSaved={() => {
          // Refresh list in background. The drawer closes itself after an
          // edit save; after a create it stays open to attach documents.
          loadData();
        }}
        onDeleted={() => {
          setDrawer((d) => ({ ...d, open: false }));
          loadData();
        }}
        onBulkImport={() => {
          // Close the create form, open the import panel on the left.
          setDrawer((d) => ({ ...d, open: false }));
          setImportOpen(true);
        }}
      />

      <ContractImportDrawer
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={(result) => {
          loadData();
          setSuccessNotification({
            title: 'Import complete',
            message: `Created ${result.created} contract${result.created === 1 ? '' : 's'}.`,
          });
        }}
      />

      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}

      {successNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="success"
            title={successNotification.title}
            message={successNotification.message}
            showClose={true}
            onClose={() => setSuccessNotification(null)}
            autoDismiss={3000}
          />
        </NotificationContainer>
      )}
    </div>
  );
}
